import React from 'react';
import Icon from 'components/AppIcon';

/**
 * Rejection analytics shown over the list: totals, top reasons and trends.
 * Lived inline in index.jsx as ~320 lines of markup between the table and the
 * bulk-action modals.
 */
const RejectionAnalyticsModal = ({ analytics, onClose }) => {
  if (!analytics) return null;

  return (
    <div className="fixed inset-0 bg-secondary-900 bg-opacity-50 flex items-center justify-center z-1001 p-4">
      <div className="bg-surface rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-secondary-200">
          <div>
            <h2 className="text-2xl font-bold text-text-primary">
              Rejection Analytics Dashboard
            </h2>
            <p className="text-text-secondary">
              Comprehensive insights into rejected biodata patterns
            </p>
          </div>
          <button
            onClick={() => onClose()}
            className="p-2 text-secondary-600 hover:bg-secondary-100 rounded-md transition-colors duration-150"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[75vh]">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-error-50 border border-error-200 rounded-lg p-6">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-error-100 rounded-full">
                  <Icon name="XCircle" size={24} className="text-error" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-error-800">
                    {analytics?.totalRejected || 0}
                  </h3>
                  <p className="text-sm text-error-600">Total Rejected</p>
                </div>
              </div>
            </div>

            <div className="bg-warning-50 border border-warning-200 rounded-lg p-6">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-warning-100 rounded-full">
                  <Icon name="Clock" size={24} className="text-warning" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-warning-800">
                    {analytics?.recentRejections || 0}
                  </h3>
                  <p className="text-sm text-warning-600">
                    Recent (7 days)
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-success-50 border border-success-200 rounded-lg p-6">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-success-100 rounded-full">
                  <Icon
                    name="CheckCircle"
                    size={24}
                    className="text-success"
                  />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-success-800">
                    {analytics?.editPermissionsGranted || 0}
                  </h3>
                  <p className="text-sm text-success-600">
                    Edit Permissions
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-accent-50 border border-accent-200 rounded-lg p-6">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-accent-100 rounded-full">
                  <Icon name="Percent" size={24} className="text-accent" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-accent-800">
                    {(analytics?.totalRejected || 0) > 0
                      ? Math.round(
                          ((analytics?.editPermissionsGranted || 0) /
                            (analytics?.totalRejected || 1)) *
                            100
                        )
                      : 0}
                    %
                  </h3>
                  <p className="text-sm text-accent-600">Permission Rate</p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Top Rejection Reasons */}
            <div className="card">
              <div className="p-6 border-b border-secondary-200">
                <h3 className="text-lg font-semibold text-text-primary flex items-center space-x-2">
                  <Icon
                    name="AlertTriangle"
                    size={20}
                    className="text-error"
                  />
                  <span>Top Rejection Reasons</span>
                </h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {(analytics?.topRejectionReasons || []).map(
                    (item, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between"
                      >
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <span className="flex items-center justify-center w-6 h-6 bg-error-100 text-error-800 rounded-full text-xs font-medium">
                              {index + 1}
                            </span>
                            <span className="text-sm font-medium text-text-primary truncate">
                              {item.reason}
                            </span>
                          </div>
                          <div className="mt-2 ml-9">
                            <div className="w-full bg-secondary-200 rounded-full h-2">
                              <div
                                className="bg-error h-2 rounded-full"
                                style={{
                                  width: `${
                                    (analytics?.totalRejected || 0) > 0
                                      ? ((item?.count || 0) /
                                          (analytics?.totalRejected || 1)) *
                                        100
                                      : 0
                                  }%`,
                                }}
                              ></div>
                            </div>
                          </div>
                        </div>
                        <span className="ml-4 text-sm font-semibold text-error">
                          {item?.count || 0}
                        </span>
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>

            {/* Rejection Trends */}
            <div className="card">
              <div className="p-6 border-b border-secondary-200">
                <h3 className="text-lg font-semibold text-text-primary flex items-center space-x-2">
                  <Icon
                    name="TrendingUp"
                    size={20}
                    className="text-accent"
                  />
                  <span>6-Month Trend</span>
                </h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {(analytics?.rejectionTrends || []).map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between"
                    >
                      <span className="text-sm font-medium text-text-secondary">
                        {item.month}
                      </span>
                      <div className="flex items-center space-x-3 flex-1 ml-4">
                        <div className="flex-1 bg-secondary-200 rounded-full h-3">
                          <div
                            className="bg-accent h-3 rounded-full transition-all duration-500"
                            style={{
                              width: `${Math.max(
                                ((item?.count || 0) /
                                  Math.max(
                                    ...(
                                      analytics?.rejectionTrends || []
                                    ).map((t) => t?.count || 0),
                                    1
                                  )) *
                                  100,
                                5
                              )}%`,
                            }}
                          ></div>
                        </div>
                        <span className="text-sm font-semibold text-accent min-w-[2rem] text-right">
                          {item?.count || 0}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Recruiter Statistics */}
            <div className="card lg:col-span-2">
              <div className="p-6 border-b border-secondary-200">
                <h3 className="text-lg font-semibold text-text-primary flex items-center space-x-2">
                  <Icon
                    name="Building"
                    size={20}
                    className="text-primary"
                  />
                  <span>Top Recruiters by Rejections</span>
                </h3>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {(analytics?.recruiterStats || []).map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center space-x-4 p-4 bg-secondary-50 rounded-lg"
                    >
                      <div className="flex items-center justify-center w-8 h-8 bg-primary-100 text-primary-800 rounded-full text-sm font-medium">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-text-primary">
                          {item?.recruiter || "Unknown"}
                        </h4>
                        <div className="mt-1 bg-secondary-200 rounded-full h-2">
                          <div
                            className="bg-primary h-2 rounded-full"
                            style={{
                              width: `${
                                (analytics?.totalRejected || 0) > 0
                                  ? ((item?.count || 0) /
                                      (analytics?.totalRejected || 1)) *
                                    100
                                  : 0
                              }%`,
                            }}
                          ></div>
                        </div>
                      </div>
                      <span className="text-lg font-semibold text-primary">
                        {item?.count || 0}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Action Insights */}
          <div className="mt-8 p-6 bg-gradient-to-r from-primary-50 to-accent-50 border border-primary-200 rounded-lg">
            <h3 className="text-lg font-semibold text-primary-800 mb-4 flex items-center space-x-2">
              <Icon name="Lightbulb" size={20} />
              <span>Insights & Recommendations</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {(analytics?.recentRejections || 0) >
                (analytics?.totalRejected || 0) * 0.3 && (
                <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
                  <Icon
                    name="AlertTriangle"
                    size={16}
                    className="text-warning mb-2"
                  />
                  <p className="text-sm text-warning-800">
                    <strong>High recent activity:</strong>{" "}
                    {analytics?.recentRejections || 0} rejections in the
                    last 7 days.
                  </p>
                </div>
              )}
              {(analytics?.editPermissionsGranted || 0) /
                (analytics?.totalRejected || 1) >
                0.5 && (
                <div className="bg-success-50 border border-success-200 rounded-lg p-4">
                  <Icon
                    name="CheckCircle"
                    size={16}
                    className="text-success mb-2"
                  />
                  <p className="text-sm text-success-800">
                    <strong>Good recovery rate:</strong> Over 50% of
                    rejections have been granted edit permissions.
                  </p>
                </div>
              )}
              {(analytics?.topRejectionReasons || []).length > 0 && (
                <div className="bg-accent-50 border border-accent-200 rounded-lg p-4">
                  <Icon
                    name="Target"
                    size={16}
                    className="text-accent mb-2"
                  />
                  <p className="text-sm text-accent-800">
                    <strong>Focus area:</strong> "
                    {(analytics?.topRejectionReasons || [])[0]?.reason ||
                      "Unknown"}
                    " is the top rejection reason.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end p-6 border-t border-secondary-200 bg-secondary-50">
          <button
            onClick={() => onClose()}
            className="px-6 py-2 bg-secondary-600 text-white rounded-md hover:bg-secondary-700 transition-colors duration-150"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default RejectionAnalyticsModal;
